import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Avatar, Badge, LoadingOverlay } from '@mantine/core'
import { IconArchive, IconArchiveOff, IconMail, IconPencilPlus } from '@tabler/icons-react'
import dayjs from 'dayjs'
import { formatHumanName, normalizeErrorString } from '@medplum/core'
import { useMedplum, useMedplumProfile } from '@medplum/react'
import { useSubscription } from '@medplum/react-hooks'
import { showNotification } from '@mantine/notifications'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import { useSessionState } from '@/hooks/useSessionState'
import { logError } from '@/utils/errorUtils'
import {
  getThreadLabels,
  getCommunicationFromSubscriptionBundle,
  isArchivedThread,
  THREAD_LABEL_URGENT,
  THREAD_LABEL_FOLLOW_UP,
  updateThreadArchivedState,
} from '@/pages/Messages/service'
import { getOtherParticipants } from '@/pages/Messages/presentation'
import { fetchThreads, type MiddlewareThreadSummary } from '../service'
import { CollapsibleSectionCard, FilterPills } from './shared'
import { ComposeMessageModal } from './ComposeMessageModal'
import { ThreadPane } from './ThreadPane'

type FilterId = 'all' | 'urgent' | 'follow-up' | 'archived'

const AVATAR_COLORS = [
  '#ef4444',
  '#f97316',
  '#3b82f6',
  '#8b5cf6',
  '#10b981',
  '#6366f1',
  '#ec4899',
  '#14b8a6',
]

function avatarColor(id: string): string {
  const hash = id.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
  return AVATAR_COLORS[hash % AVATAR_COLORS.length]
}

function getInitials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
}

function formatThreadTime(iso?: string): string {
  if (!iso) return ''
  const d = dayjs(iso)
  const today = dayjs().startOf('day')
  if (d.isAfter(today)) return d.format('h:mm A')
  if (d.isAfter(today.subtract(1, 'day'))) return 'Yesterday'
  if (d.isAfter(today.subtract(7, 'day'))) return d.format('dddd')
  return d.format('MMM D')
}

type ThreadRowProps = {
  item: MiddlewareThreadSummary
  currentPractitionerId: string
  isSelected: boolean
  onSelect: (item: MiddlewareThreadSummary) => void
  onArchiveToggle: (item: MiddlewareThreadSummary) => void
}

const ThreadRow = React.memo(
  function ThreadRow({
    item,
    currentPractitionerId,
    isSelected,
    onSelect,
    onArchiveToggle,
  }: ThreadRowProps) {
    const labels = getThreadLabels(item.thread)
    const isUrgent = labels.includes(THREAD_LABEL_URGENT)
    const isFollowUp = labels.includes(THREAD_LABEL_FOLLOW_UP)
    const isArchived = isArchivedThread(item.thread)
    // Use the server-computed status directly (same source as the full Messages page).
    const isUnread = item.status === 'unread'

    // Everyone but the current user, creator first. A group thread lists the
    // first two and collapses the rest, so the row never implies a 1:1.
    const otherParticipants = getOtherParticipants(item, currentPractitionerId)
    const primaryName = otherParticipants[0]
      ? formatHumanName(otherParticipants[0].practitioner.name?.[0])
      : (item.thread.sender?.display ?? 'Unknown')
    const extraParticipants = otherParticipants.length - 1
    const displayName =
      extraParticipants > 0
        ? `${primaryName}, ${formatHumanName(otherParticipants[1].practitioner.name?.[0])}${
            extraParticipants > 1 ? ` +${extraParticipants - 1}` : ''
          }`
        : primaryName
    // Avatar stays keyed to one person: initials of a joined list are nonsense.
    const initials = getInitials(primaryName)
    const color = avatarColor(otherParticipants[0]?.practitioner.id ?? primaryName)

    const subject = item.thread.topic?.text ?? '(No subject)'
    const preview = item.latestMessage?.payload?.[0]?.contentString ?? ''
    const time = formatThreadTime(item.latestMessage?.sent ?? item.thread.sent)

    const relatedPatient = item.relatedPatient
      ? formatHumanName(item.relatedPatient.name?.[0])
      : null

    return (
      <div
        onClick={() => onSelect(item)}
        className={`flex cursor-pointer items-start gap-3 border-b border-gray-100 px-4 py-3 transition-colors hover:bg-gray-50 ${isSelected ? 'bg-gray-50' : ''}`}
      >
        <Avatar size={40} radius="xl" style={{ backgroundColor: color, flexShrink: 0 }}>
          <span className="text-xs font-semibold text-white">{initials}</span>
        </Avatar>

        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-gray-900">
              Conversation with: {displayName}
            </span>
            {isUrgent && (
              <Badge size="xs" variant="light" color="red" radius="sm">
                URGENT
              </Badge>
            )}
            {isFollowUp && (
              <Badge size="xs" variant="light" color="blue" radius="sm">
                FOLLOW-UP
              </Badge>
            )}
          </div>
          <p
            className={`mb-0.5 truncate text-xs ${isUnread ? 'font-semibold text-gray-700' : 'text-gray-500'}`}
          >
            Topic: {subject}
            {relatedPatient ? ` (Patient: ${relatedPatient})` : ''}
          </p>
          {preview && <p className="truncate text-xs text-gray-400">{preview}</p>}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5 pt-0.5">
          <span className="text-xs whitespace-nowrap text-gray-500">{time}</span>
          <div className="flex items-center gap-1.5">
            {isUnread && (
              <span
                className="h-2 w-2 rounded-full bg-red-500"
                title="New message"
                aria-label="New message"
              />
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onArchiveToggle(item)
              }}
              title={isArchived ? 'Unarchive' : 'Archive'}
              className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              {isArchived ? <IconArchiveOff size={14} /> : <IconArchive size={14} />}
            </button>
          </div>
        </div>
      </div>
    )
  },
  (prev, next) =>
    prev.isSelected === next.isSelected &&
    prev.currentPractitionerId === next.currentPractitionerId &&
    prev.onSelect === next.onSelect &&
    prev.onArchiveToggle === next.onArchiveToggle &&
    prev.item.thread.id === next.item.thread.id &&
    prev.item.thread.status === next.item.thread.status &&
    prev.item.thread.meta?.lastUpdated === next.item.thread.meta?.lastUpdated &&
    prev.item.status === next.item.status &&
    prev.item.latestMessage?.id === next.item.latestMessage?.id &&
    prev.item.latestMessage?.sent === next.item.latestMessage?.sent,
)

export const Messages = () => {
  const middleware = useMiddlewareClient()
  const medplum = useMedplum()
  const profile = useMedplumProfile()
  const practitionerId = profile?.resourceType === 'Practitioner' ? (profile.id ?? '') : ''

  // Expand/collapse, filter and selection are kept for the session so leaving
  // Home and coming back reopens the section as the user left it (PD-1206).
  const [open, setOpen] = useSessionState('home:messages:open', true)
  const [filter, setFilter] = useSessionState<FilterId>('home:messages:filter', 'all')
  const [composeOpen, setComposeOpen] = useState(false)
  const [threads, setThreads] = useState<MiddlewareThreadSummary[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedThread, setSelectedThread] = useSessionState<MiddlewareThreadSummary | null>(
    'home:messages:selectedThread',
    null,
  )

  const handleThreadSelect = useCallback(
    (item: MiddlewareThreadSummary) => {
      setSelectedThread((prev) => (prev?.thread.id === item.thread.id ? null : item))
    },
    [setSelectedThread],
  )

  const handleArchiveToggle = useCallback(
    async (item: MiddlewareThreadSummary) => {
      const archived = isArchivedThread(item.thread)
      const nextStatus = archived ? 'in-progress' : 'completed'
      const prevStatus = item.thread.status

      const applyStatus = (status: typeof nextStatus | typeof prevStatus) => {
        const patch = (t: MiddlewareThreadSummary) =>
          t.thread.id === item.thread.id ? { ...t, thread: { ...t.thread, status } } : t
        setThreads((prev) => prev.map(patch))
        setAllThreads((prev) => prev.map(patch))
      }

      applyStatus(nextStatus)

      try {
        await updateThreadArchivedState(medplum, item.thread, !archived)
      } catch (err) {
        applyStatus(prevStatus)
        showNotification({
          color: 'red',
          title: archived ? 'Unable to unarchive thread' : 'Unable to archive thread',
          message: normalizeErrorString(err),
        })
      }
    },
    [medplum],
  )

  const [allThreads, setAllThreads] = useState<MiddlewareThreadSummary[]>([])

  const loadThreads = useCallback(async () => {
    if (!practitionerId) return
    setIsLoading(true)
    try {
      const result = await fetchThreads(middleware, {
        ...(filter === 'urgent' ? { onlyUrgent: true } : {}),
        ...(filter === 'follow-up' ? { onlyFollowUps: true } : {}),
      })
      setThreads(result)
      // When showing all threads, reuse the result for pill counts — no second request.
      if (filter === 'all') setAllThreads(result)
    } catch (err) {
      logError('home', 'Failed to fetch threads', err)
    } finally {
      setIsLoading(false)
    }
  }, [middleware, practitionerId, filter])

  useEffect(() => {
    loadThreads()
  }, [loadThreads])

  // Live subscription — mirrors MessagesUnreadContext pattern exactly.
  // Fires whenever a new Communication arrives addressed to the current
  // practitioner; triggers a thread list refresh so the unread count and
  // the "N conversations has new messages" notice update in real time.
  useSubscription(
    practitionerId ? `Communication?recipient=Practitioner/${practitionerId}` : undefined,
    (bundle) => {
      if (!getCommunicationFromSubscriptionBundle(bundle)) return
      loadThreads()
    },
  )

  // Fetch unfiltered counts only when a filter is active — avoids a duplicate
  // request on mount when filter === 'all' (handled inside loadThreads above).
  useEffect(() => {
    if (!practitionerId || filter === 'all') return
    fetchThreads(middleware)
      .then(setAllThreads)
      .catch((err) => logError('home', 'Failed to fetch all threads for counts', err))
  }, [middleware, practitionerId, filter])

  const FILTERS: ReadonlyArray<{ id: FilterId; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'urgent', label: 'Urgent' },
    { id: 'follow-up', label: 'Follow-up' },
    { id: 'archived', label: 'Archived' },
  ]

  const unreadCount = useMemo(
    () => allThreads.filter((t) => t.status === 'unread').length,
    [allThreads],
  )

  const visibleThreads = useMemo(
    () =>
      filter === 'archived'
        ? threads.filter((t) => isArchivedThread(t.thread))
        : threads.filter((t) => !isArchivedThread(t.thread)),
    [threads, filter],
  )

  return (
    <div className="flex flex-col gap-3">
      <CollapsibleSectionCard
        icon={<IconMail size={20} className="text-[rgb(229,83,61)]" />}
        title="Messages"
        count={0}
        countClassName=""
        headerExtras={
          unreadCount > 0 ? (
            <span className="text-xs font-semibold text-[rgb(229,83,61)]">
              {unreadCount} conversation{unreadCount !== 1 ? 's' : ''} has new messages
            </span>
          ) : null
        }
        open={open}
        onToggle={() => setOpen((v) => !v)}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 px-4 pt-2 pb-3">
          <FilterPills options={FILTERS} value={filter} onChange={setFilter} />
          <button
            type="button"
            onClick={() => setComposeOpen(true)}
            className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-md px-2.5 py-[3px] text-[11px] font-semibold text-white transition-colors"
            style={{ backgroundColor: 'rgb(229, 83, 61)' }}
          >
            <IconPencilPlus size={12} />
            Compose
          </button>
        </div>

        <div className="relative max-h-[520px] overflow-y-auto">
          <LoadingOverlay
            visible={isLoading && threads.length === 0}
            zIndex={10}
            overlayProps={{ blur: 1 }}
            loaderProps={{ size: 'sm', color: 'blue' }}
          />
          {!isLoading && visibleThreads.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-gray-400">
              {filter === 'archived' ? 'No archived messages' : 'No messages'}
            </p>
          ) : (
            visibleThreads.map((item) => {
              const isSelected = selectedThread?.thread.id === item.thread.id
              return (
                <React.Fragment key={item.thread.id}>
                  <ThreadRow
                    item={item}
                    currentPractitionerId={practitionerId}
                    isSelected={isSelected}
                    onSelect={handleThreadSelect}
                    onArchiveToggle={handleArchiveToggle}
                  />
                  {isSelected && (
                    <ThreadPane threadSummary={item} onThreadRead={() => loadThreads()} />
                  )}
                </React.Fragment>
              )
            })
          )}
        </div>
      </CollapsibleSectionCard>

      <ComposeMessageModal
        opened={composeOpen}
        onClose={() => setComposeOpen(false)}
        onCreated={() => {
          loadThreads()
        }}
      />
    </div>
  )
}
